import { createContext, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { handoffUrl, issueHandoff } from '../auth/handoff';

/**
 * Open a console screen in a NEW top-level tab, signed in.
 *
 * A framed page cannot route to a config screen in place: the frame's
 * credentials are the dashboard's, held in this frame and never persisted, so
 * every other route here meets RequireAuth with no session — and OAuth consent
 * pages refuse to render in a frame anyway. The tab goes through /sso with a
 * one-time code minted here, and arrives signed in.
 *
 * The window is opened SYNCHRONOUSLY, before the code exists: a popup blocker
 * only trusts a window opened in the click's own tick, and minting is a round
 * trip. It parks on about:blank for that moment, then gets replaced. The
 * handover is a convenience, never a gate: if minting fails the tab still
 * opens on the screen and asks for a password.
 */
export function openConsoleTab(path: string): void {
  const origin = window.location.origin;
  const tab = window.open('', '_blank');
  const go = (url: string) => {
    // replace(), not assign(): the blank placeholder must not become a
    // history entry the agent can go Back to.
    if (tab) tab.location.replace(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };
  void issueHandoff().then(
    (code) => go(handoffUrl(origin, code, path)),
    () => go(`${origin}${path}`),
  );
}

/**
 * Whether the screen below is framed by the dispatch dashboard. The
 * Communications module answers the same question through its CommsSource;
 * the screens framed on their own (the website pages) have no source, so they
 * say it here instead.
 */
export const FramedContext = createContext(false);

/**
 * Go to a console screen: in place standalone, in a signed-in new tab when
 * framed. The one way a screen that is ALSO framed may link to /connect or
 * /inboxes/:id — a router Link there lands the frame on the sign-in form.
 */
export function useConsoleNav(): (path: string) => void {
  const framed = useContext(FramedContext);
  const navigate = useNavigate();
  return useCallback(
    (path: string) => {
      if (framed) openConsoleTab(path);
      else navigate(path);
    },
    [framed, navigate],
  );
}
